'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { toast as sonner } from 'sonner';
import { Button } from './Button';
import { Icon } from './Icon';
import { Toaster } from './ui/sonner';
/** Сколько тостов видно разом. Остальные ждут очереди. */
const VISIBLE_AT_ONCE = 2;
/** Сколько живёт тост по умолчанию. */
const DEFAULT_DURATION = 5000;
/*
 * ПОДЛОЖКА НЕПРОЗРАЧНАЯ. Находка N-02 повторного аудита: фон тона был
 * `bg-primary/10` без подложки, и сквозь «Симулятор запущен» читались «Гид»
 * и значки подвала левой колонки. Тон подмешивается к `--popover` 10 % -
 * тем же приёмом, каким пресет строит наведение `secondary`: цвет остаётся
 * токеном, а прозрачности нет.
 */
const TONE = {
    success: 'border-primary/40 bg-[color-mix(in_oklch,var(--popover),var(--primary)_10%)]',
    error: 'border-destructive/40 bg-[color-mix(in_oklch,var(--popover),var(--destructive)_10%)]',
    warning: 'border-warning/40 bg-[color-mix(in_oklch,var(--popover),var(--warning)_10%)]',
    neutral: 'border-foreground/15 bg-popover',
};
/**
 * Область тостов. Ставится один раз в корне приложения.
 *
 * Имя оставлено прежним (`ToastProvider`), хотя контекста здесь больше нет:
 * его импортируют оба приложения, и переименование ради точности стоило бы
 * правок в местах, которые эта единица не должна трогать.
 */
export function ToastProvider({ children }) {
    return (_jsxs(_Fragment, { children: [children, _jsx(Toaster
            /*
             * ПОЛОСА ТОСТОВ ЗАДАЁТСЯ СНАРУЖИ - ТЕМ, КТО ЗНАЕТ, ГДЕ НЕТ ОРГАНОВ.
             *
             * История: внизу по центру тост ложился на окно (единица 11), в
             * правом нижнем углу - на ленту гифтеров (находка 13), в левом
             * нижнем - на подвал левой колонки с кнопками (находка N-02). Каждый
             * раз угол выбирали здесь, не зная раскладки экрана.
             *
             * Теперь угол - левый нижний, а смещение и ширину пишет консоль в
             * переменные `--toast-left`, `--toast-bottom`, `--toast-width`
             * (`apps/dashboard/src/console/toastLane.ts`): полоса стоит в центре
             * консоли, где нет органов управления. Без консоли (экраны входа)
             * действуют запасные 16 px от угла. Проверяется воротами
             * `e2e/class-toast.spec.ts` против каждого органа управления экрана.
             */
            , { 
                /*
                 * ПОЛОСА ТОСТОВ ЗАДАЁТСЯ СНАРУЖИ - ТЕМ, КТО ЗНАЕТ, ГДЕ НЕТ ОРГАНОВ.
                 *
                 * История: внизу по центру тост ложился на окно (единица 11), в
                 * правом нижнем углу - на ленту гифтеров (находка 13), в левом
                 * нижнем - на подвал левой колонки с кнопками (находка N-02). Каждый
                 * раз угол выбирали здесь, не зная раскладки экрана.
                 *
                 * Теперь угол - левый нижний, а смещение и ширину пишет консоль в
                 * переменные `--toast-left`, `--toast-bottom`, `--toast-width`
                 * (`apps/dashboard/src/console/toastLane.ts`): полоса стоит в центре
                 * консоли, где нет органов управления. Без консоли (экраны входа)
                 * действуют запасные 16 px от угла. Проверяется воротами
                 * `e2e/class-toast.spec.ts` против каждого органа управления экрана.
                 */
                position: "bottom-left", visibleToasts: VISIBLE_AT_ONCE, 
                /* Своя разметка - свои отступы: у Sonner они рассчитаны на его
                   собственный вид, а наш тост шире по содержимому. */
                gap: 8, offset: { left: 'var(--toast-left, 16px)', bottom: 'var(--toast-bottom, 16px)' }, mobileOffset: { left: 'var(--toast-left, 16px)', bottom: 'var(--toast-bottom, 16px)' }, 
                /*
                 * ТОСТ ЦЕЛИКОМ В ОКНЕ - И НА ВХОДЕ, И НА ВЫХОДЕ (единица 22).
                 *
                 * Sonner вводит тост снизу и уводит вниз: сдвиг на собственную
                 * высоту (`--y: translateY(100%)`). Полоса стоит в 12 px от нижней
                 * кромки, и полсекунды входа и выхода тост был обрезан окном -
                 * владелец увидел «Симулятор запущен» наполовину. Ворота единицы 21
                 * мерили тост в покое и этого не видели. Теперь вход и выход -
                 * только прозрачностью: у нового и уходящего тоста сдвиг тот же,
                 * что в покое. Стопка из двух тостов (`--offset`) не тронута.
                 */
                toastOptions: {
                    unstyled: true,
                    classNames: {
                        toast: 'w-full data-[mounted=false]:[--y:translateY(0)]! data-[removed=true]:data-[front=true]:[--y:translateY(0)]!',
                    },
                } })] }));
}
/**
 * ОЧЕРЕДЬ ТОСТОВ - НАША, А НЕ БИБЛИОТЕЧНАЯ.
 *
 * У Sonner есть `visibleToasts`, но он ПРЯЧЕТ лишние своим оформлением, а
 * не убирает их из документа: с нашей разметкой (`unstyled`) третий тост
 * оставался видимым, и ворота поймали стопку из трёх. Правило продукта - «не
 * больше двух ВИДНО РАЗОМ, остальные ждут очереди», поэтому очередь держим
 * сами: третий не показывается, пока не уйдёт первый.
 */
const waiting = [];
let shown = 0;
/**
 * Пауза перед показом следующего из очереди.
 *
 * Sonner убирает узел ушедшего тоста не в тот же миг - он доигрывает уход.
 * Показать следующего сразу значит на долю секунды получить в документе три
 * тоста: правило нарушено ровно настолько, насколько его успевает увидеть
 * проверка, - и ровно настолько, насколько его успевает увидеть человек.
 */
const AFTER_LEAVING = 300;
function release() {
    shown = Math.max(0, shown - 1);
    setTimeout(() => {
        const next = waiting.shift();
        if (next !== undefined)
            present(next);
    }, AFTER_LEAVING);
}
/** Показать тост. Возвращается из хука ради прежнего вызова `toast.show(...)`. */
function show(options) {
    if (shown >= VISIBLE_AT_ONCE) {
        waiting.push(options);
        return;
    }
    present(options);
}
function present(options) {
    const tone = options.tone ?? 'neutral';
    const duration = options.duration ?? DEFAULT_DURATION;
    shown += 1;
    sonner.custom((id) => (_jsxs("div", { "data-toast": tone === 'success' ? 'success' : 'message', className: `text-popover-foreground pointer-events-auto flex w-[var(--toast-width,20rem)] max-w-[calc(100vw-2rem)] items-start gap-3 rounded-lg border p-3 shadow-lg ${TONE[tone]}`, children: [tone !== 'neutral' ? _jsx(Icon, { name: tone, size: "lg", className: "mt-1" }) : null, _jsxs("div", { className: "text-foreground min-w-0 flex-1", children: [_jsx("div", { className: "font-semibold", children: options.title }), options.text !== undefined ? _jsx("div", { className: "mt-1 text-sm", children: options.text }) : null] }), _jsx(Button, { variant: "ghost", size: "s", iconOnly: true, "data-toast-dismiss": true, className: "-me-2 -mt-1", "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u0435", onClick: () => {
                    sonner.dismiss(id);
                }, children: _jsx(Icon, { name: "dismiss" }) })] })), {
        /* 0 у нас значит «висит до закрытия вручную», у Sonner - Infinity. */
        duration: duration <= 0 ? Number.POSITIVE_INFINITY : duration,
        onAutoClose: release,
        onDismiss: release,
    });
}
/**
 * Хук показа тоста.
 *
 * Прежний интерфейс сохранён дословно: `const toast = useToast();
 * toast.show({ tone, title, text })`. Двести мест продукта его зовут, и
 * переписывать их ради смены библиотеки под капотом незачем.
 */
export function useToast() {
    return useMemo(() => ({ show }), []);
}
//# sourceMappingURL=Toast.js.map